import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Package, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  FileText, 
  Upload,
  Check,
  AlertCircle,
  Clock,
  Award,
  LogOut,
  CheckCircle,
  XCircle,
  Loader2,
  Calendar
} from 'lucide-react';

const TeamMemberDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [teamMember, setTeamMember] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  const [profileData, setProfileData] = useState({
    bio: '',
    yearsExperience: '',
    hoursLogged: '',
    assetTypesExperience: []
  });
  
  const assetTypes = [
    'Pontoon', 'Yacht', 'Sailasset', 'Speedasset', 
    'Fishing Asset', 'Catamaran', 'Double-Decker', 'Other'
  ];
  
  useEffect(() => {
    fetchTeamMemberData();
  }, []);
  
  const fetchTeamMemberData = async () => {
    try {
      setLoading(true);
      const teamMemberId = localStorage.getItem('teamMemberId');
      
      if (!teamMemberId) {
        toast.error('Please sign up first');
        navigate('/teamMember-signup');
        return;
      }
      
      // Fetch team_member profile
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/teamMembers/${teamMemberId}/public-profile`);
      
      if (!response.ok) {
        throw new Error('Failed to load profile');
      }
      
      const data = await response.json();
      setTeamMember(data);
      setDocuments(data.documents || []);
      
      setProfileData({
        bio: data.bio || '',
        yearsExperience: data.yearsExperience || '',
        hoursLogged: data.hoursLogged || '',
        assetTypesExperience: data.assetTypesExperience || []
      });
      
    } catch (error) {
      console.error('Error fetching team_member data:', error);
      toast.error('Failed to load your profile');
    } finally {
      setLoading(false);
    }
  };
  
  const calculateProfileCompletion = () => {
    if (!teamMember) return 0;
    
    let completed = 0;
    const total = 7;
    
    // Basic info (always completed from signup)
    if (teamMember.name) completed++;
    if (teamMember.email) completed++;
    if (teamMember.location) completed++;
    
    // Profile details
    if (teamMember.bio) completed++;
    if (teamMember.yearsExperience) completed++;
    
    // Documents
    const hasLicense = documents.some(d => d.type === 'uscg_license');
    const hasId = documents.some(d => d.type === 'other'); // ID as "other" for now
    
    if (hasLicense) completed++;
    if (hasId) completed++;
    
    return Math.round((completed / total) * 100);
  };
  
  const handleFileUpload = async (e, documentType) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    // Validate file type
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      toast.error('Only PDF and images (JPEG, PNG) are allowed');
      return;
    }
    
    try {
      setUploading(true);
      const teamMemberId = localStorage.getItem('teamMemberId');
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', documentType);
      formData.append('title', getDocumentTitle(documentType));
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/teamMembers/${teamMemberId}/documents`,
        {
          method: 'POST',
          body: formData
        }
      );
      
      if (!response.ok) {
        throw new Error('Upload failed');
      }
      
      toast.success('✅ Document uploaded successfully!');
      fetchTeamMemberData(); // Refresh data
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };
  
  const getDocumentTitle = (type) => {
    const titles = {
      'uscg_license': "USCG TeamMember's License",
      'double_decker': 'Double-Decker Certification',
      'first_aid': 'First Aid Certificate',
      'cpr': 'CPR Certificate',
      'insurance': 'Insurance Certificate',
      'logbook': 'Logbook',
      'other': 'Government ID'
    };
    return titles[type] || 'Document';
  };
  
  const toggleAssetType = (type) => {
    setProfileData(prev => ({
      ...prev,
      assetTypesExperience: prev.assetTypesExperience.includes(type)
        ? prev.assetTypesExperience.filter(t => t !== type)
        : [...prev.assetTypesExperience, type]
    }));
  };
  
  const handleSaveProfile = async () => {
    try {
      const teamMemberId = localStorage.getItem('teamMemberId');
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/teamMembers/${teamMemberId}/profile`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(profileData)
        }
      );
      
      if (!response.ok) {
        throw new Error('Update failed');
      }
      
      toast.success('✅ Profile updated!');
      setEditMode(false);
      fetchTeamMemberData();
      
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update profile');
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem('teamMemberId');
    localStorage.removeItem('teamMemberEmail');
    localStorage.removeItem('teamMemberName');
    navigate('/teamMember-signup');
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }
  
  const profileCompletion = calculateProfileCompletion();
  const completionColor = profileCompletion >= 80 ? 'bg-green-500' : profileCompletion >= 50 ? 'bg-yellow-500' : 'bg-red-500';
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Package className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">TeamMember Dashboard</h1>
              <p className="text-sm text-gray-600">Welcome back, {teamMember?.name}!</p>
            </div>
          </div>
          <Button onClick={handleLogout} variant="outline" size="sm">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        {/* Status Banner */}
        <Card className={`border-l-4 ${
          team_member?.status === 'active' ? 'border-green-500 bg-green-50' :
          team_member?.status === 'rejected' ? 'border-red-500 bg-red-50' :
          'border-yellow-500 bg-yellow-50'
        }`}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {teamMember?.status === 'active' ? (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                ) : team_member?.status === 'rejected' ? (
                  <XCircle className="w-6 h-6 text-red-600" />
                ) : (
                  <Clock className="w-6 h-6 text-yellow-600" />
                )}
                <div>
                  <h3 className="font-semibold text-lg">
                    {teamMember?.status === 'active' ? '✅ Profile Approved!' :
                     team_member?.status === 'rejected' ? '❌ Profile Rejected' :
                     '⏳ Pending Approval'}
                  </h3>
                  <p className="text-sm text-gray-700">
                    {teamMember?.status === 'active' 
                      ? 'Your profile is live! Operators can now find and hire you.' 
                      : team_member?.status === 'rejected'
                      ? 'Please contact support for more information.'
                      : 'Complete your profile and upload documents to get approved faster.'}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Profile Completion */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Profile Completion</span>
              <span className="text-2xl font-bold">{profileCompletion}%</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
              <div 
                className={`${completionColor} h-4 rounded-full transition-all duration-500`}
                style={{ width: `${profileCompletion}%` }}
              />
            </div>
            <p className="text-sm text-gray-600">
              {profileCompletion >= 80 
                ? '🎉 Great! Your profile is complete and ready for review.' 
                : 'Upload documents and complete your profile to get approved.'}
            </p>
          </CardContent>
        </Card>
        
        {/* Profile Info */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <User className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-600">Name</p>
                  <p className="font-medium">{teamMember?.name}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Mail className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-medium">{teamMember?.email}</p>
                </div>
              </div>
              
              {teamMember?.phone && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Phone className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <p className="font-medium">{teamMember?.phone}</p>
                  </div>
                </div>
              )}
              
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <MapPin className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-600">Location</p>
                  <p className="font-medium">{teamMember?.location}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Experience */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-blue-600" />
                  Experience & Skills
                </span>
                {!editMode && (
                  <Button onClick={() => setEditMode(true)} size="sm" variant="outline">
                    Edit
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {editMode ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Years of Experience
                    </label>
                    <input
                      type="number"
                      value={profileData.yearsExperience}
                      onChange={(e) => setProfileData({...profileData, yearsExperience: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="5"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Hours Logged
                    </label>
                    <input
                      type="number"
                      value={profileData.hoursLogged}
                      onChange={(e) => setProfileData({...profileData, hoursLogged: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="1000"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Asset Types Experience
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {assetTypes.map(type => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => toggleAssetType(type)}
                          className={`px-3 py-2 rounded-lg border text-sm transition-all ${
                            profileData.assetTypesExperience.includes(type)
                              ? 'border-blue-600 bg-blue-50 text-blue-700 font-medium'
                              : 'border-gray-300 bg-white text-gray-700'
                          }`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bio
                    </label>
                    <textarea
                      value={profileData.bio}
                      onChange={(e) => setProfileData({...profileData, bio: e.target.value})}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="Tell operators about your experience..."
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <Button onClick={handleSaveProfile} className="flex-1" style={{ background: '#007AFF' }}>
                      Save Changes
                    </Button>
                    <Button onClick={() => setEditMode(false)} variant="outline" className="flex-1">
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {teamMember?.yearsExperience && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">Experience</p>
                      <p className="font-medium">{teamMember.yearsExperience} years</p>
                    </div>
                  )}
                  
                  {teamMember?.hoursLogged && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">Hours Logged</p>
                      <p className="font-medium">{teamMember.hoursLogged} hours</p>
                    </div>
                  )}
                  
                  {teamMember?.assetTypesExperience?.length > 0 && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">Asset Types</p>
                      <p className="font-medium">{teamMember.assetTypesExperience.join(', ')}</p>
                    </div>
                  )}
                  
                  {teamMember?.bio && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">Bio</p>
                      <p className="font-medium text-sm">{teamMember.bio}</p>
                    </div>
                  )}
                  
                  {!teamMember?.yearsExperience && !teamMember?.bio && (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No experience info yet. Click "Edit" to add.
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Documents */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Documents & Certifications
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Required Documents */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm text-gray-700">Required Documents</h3>
                
                {/* TeamMember's License */}
                <div className="border border-gray-300 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium text-sm">TeamMember's License *</p>
                    {documents.find(d => d.type === 'uscg_license') ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                  {documents.find(d => d.type === 'uscg_license') ? (
                    <p className="text-xs text-green-600">✅ Uploaded</p>
                  ) : (
                    <label className="block">
                      <input
                        type="file"
                        accept=".pdf,image/*"
                        onChange={(e) => handleFileUpload(e, 'uscg_license')}
                        className="hidden"
                        disabled={uploading}
                      />
                      <Button size="sm" variant="outline" className="w-full" disabled={uploading}>
                        <Upload className="w-4 h-4 mr-2" />
                        {uploading ? 'Uploading...' : 'Upload PDF/Image'}
                      </Button>
                    </label>
                  )}
                </div>
                
                {/* Government ID */}
                <div className="border border-gray-300 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium text-sm">Government ID *</p>
                    {documents.find(d => d.type === 'other' && d.title.includes('ID')) ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                  {documents.find(d => d.type === 'other' && d.title.includes('ID')) ? (
                    <p className="text-xs text-green-600">✅ Uploaded</p>
                  ) : (
                    <label className="block">
                      <input
                        type="file"
                        accept=".pdf,image/*"
                        onChange={(e) => handleFileUpload(e, 'other')}
                        className="hidden"
                        disabled={uploading}
                      />
                      <Button size="sm" variant="outline" className="w-full" disabled={uploading}>
                        <Upload className="w-4 h-4 mr-2" />
                        {uploading ? 'Uploading...' : 'Upload PDF/Image'}
                      </Button>
                    </label>
                  )}
                </div>
              </div>
              
              {/* Optional Documents */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm text-gray-700">Optional Documents</h3>
                
                {/* Logbook */}
                <div className="border border-gray-300 rounded-lg p-4">
                  <p className="font-medium text-sm mb-2">Logbook</p>
                  {documents.find(d => d.type === 'logbook') ? (
                    <p className="text-xs text-green-600">✅ Uploaded</p>
                  ) : (
                    <label className="block">
                      <input
                        type="file"
                        accept=".pdf,image/*"
                        onChange={(e) => handleFileUpload(e, 'logbook')}
                        className="hidden"
                        disabled={uploading}
                      />
                      <Button size="sm" variant="outline" className="w-full" disabled={uploading}>
                        <Upload className="w-4 h-4 mr-2" />
                        {uploading ? 'Uploading...' : 'Upload'}
                      </Button>
                    </label>
                  )}
                </div>
                
                {/* Insurance */}
                <div className="border border-gray-300 rounded-lg p-4">
                  <p className="font-medium text-sm mb-2">Insurance Certificate</p>
                  {documents.find(d => d.type === 'insurance') ? (
                    <p className="text-xs text-green-600">✅ Uploaded</p>
                  ) : (
                    <label className="block">
                      <input
                        type="file"
                        accept=".pdf,image/*"
                        onChange={(e) => handleFileUpload(e, 'insurance')}
                        className="hidden"
                        disabled={uploading}
                      />
                      <Button size="sm" variant="outline" className="w-full" disabled={uploading}>
                        <Upload className="w-4 h-4 mr-2" />
                        {uploading ? 'Uploading...' : 'Upload'}
                      </Button>
                    </label>
                  )}
                </div>
              </div>
            </div>
            
            {/* Uploaded Documents List */}
            {documents.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="font-semibold text-sm text-gray-700 mb-3">Uploaded Documents ({documents.length})</h3>
                <div className="space-y-2">
                  {documents.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-gray-500" />
                        <div>
                          <p className="font-medium text-sm">{doc.title}</p>
                          <p className="text-xs text-gray-600">
                            Uploaded: {new Date(doc.uploadedAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {doc.status === 'verified' && (
                          <span className="text-green-600 text-xs font-medium flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" />
                            Verified
                          </span>
                        )}
                        {doc.status === 'pending' && (
                          <span className="text-yellow-600 text-xs font-medium flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            Pending
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TeamMemberDashboard;
